import { CardTypesEnum } from '../../../models/CardTypesEnum';
import { IndicatorKeyEnum } from '../../../models/IndicatorKeyEnum';
import Indicator from '../../../models/Indicator';

export const getEmptyData = (card: Indicator) => {
  switch (card.type) {
    case CardTypesEnum.HORIZONTAL:
    case CardTypesEnum.VERTICAL:
    case CardTypesEnum.HALF_SIZE_VERTICAL:
    case CardTypesEnum.MIXED_VERTICAL_BAR_LINE:
    case CardTypesEnum.VERTICAL_SKYBLUE_ORANGE_BAR:
      return emptyBarChartData();
    case CardTypesEnum.RADIAL:
    case CardTypesEnum.RADIAL_TO_BARCHART:
      return emptyRadialData();
    case CardTypesEnum.TEXT:
      return { value: 0 };
    case CardTypesEnum.DOUGHNUT:
      return emptyDoughnutData(card.indicatorKey);
    case CardTypesEnum.PROGRESS:
      return emptyProgressData();
    case CardTypesEnum.PROGRESS_PROJECTION:
      return emptyProgressProjectionData();
    case CardTypesEnum.LIST:
      return [];
    case CardTypesEnum.CALENDAR:
      return [];
    case CardTypesEnum.DOUBLE_DATA_NOT_STACKED:
    case CardTypesEnum.DOUBLE_DATA_NOT_STACKED_MOUNTED_BARS:
      return emptyMultiBarData();
    case CardTypesEnum.PROGRESS_CHART:
      return { progressData: emptyProgressChartData(card.indicatorKey) };
    case CardTypesEnum.DOUBLE_DATA_PROGRESS_CHART:
      return emptyDoubleDataProgressChartData(card.indicatorKey);
    case CardTypesEnum.SIMPLE_SUMMARY:
      return {
        technicalCooperations: { value: 0, text: '' },
        supervisionPlans: { value: 0, text: '' },
      };
    case CardTypesEnum.BUBBLE_CARD_TYPE:
      return emptyBubbleData();
    case CardTypesEnum.TEXT_DOUBLE:
      return emptyTextDoubleData(card.indicatorKey);
    case CardTypesEnum.TEXT_DOUBLE_ICON:
      return emptyTextDoubleIconData(card.indicatorKey);
    case CardTypesEnum.MULTI_LINE:
      return [emptyMultiLineData()];
    default:
      return {};
  }
};

export const isDoughnutEmpty = (card: Indicator) => {
  const dataset = card.data && card.data.datasets && card.data.datasets[0];
  if (!dataset) {
    return true;
  }
  const values = dataset.projectQuantity || dataset.data || [];
  if (!values.length) {
    return true;
  }
  return values.every(value => !value || Number.isNaN(value));
};

const emptyBarChartData = () => {
  const currentYear = new Date().getFullYear();
  const lastFiveYears = Array.from({ length: 5 }, (_, i) => (currentYear - 4 + i).toString());

  return {
    datasets: [
      {
        data: [0, 0, 0, 0, 0],
        backgroundColor: ['#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0'],
        borderWidth: 0,
        label: '',
        backgroundColorId: [],
        minBarLength: 6,
        datalabels: {
          formatter: () => '0',
          anchor: 'start',
          align: 'start',
          padding: -10,
          backgroundColor: 'transparent',
          color: '#a0abc1',
          clip: false,
          font: {
            size: 13,
            family: 'Rubik',
            weight: 'bold',
          },
        },
      },
    ],
    labels: lastFiveYears,
    dataListTranslationLabels: [],
    dataQtyProjects: [],
    suggestedMax: 100,
    forceBeginAtZero: true,
    forceShowDataLabels: true,
  };
};

export { emptyBarChartData };

const emptyRadialData = () => ({
  centerText: '0',
  circles: [
    {
      key: '0',
      mouseEnterColor: '#D0DEF8',
      strokeColor: '#D0DEF8',
      value: 0,
      clickValue: '',
      strokeWidth: 5,
      strokeWidthMobile: 2,
      trailColor: 'rgba(155, 160, 170, 0.2)',
      percentageValue: 0,
      labelText: '',
      labelValue: '0',
    },
    {
      key: '1',
      mouseEnterColor: '#D0DEF8',
      strokeColor: '#D0DEF8',
      value: 0,
      clickValue: '',
      strokeWidth: 5,
      strokeWidthMobile: 2,
      trailColor: 'rgba(155, 160, 170, 0.2)',
      percentageValue: 0,
      labelText: '',
      labelValue: '0',
    },
  ],
});

const emptyDoughnutData = (indicatorKey?: string) => {
  if (indicatorKey === IndicatorKeyEnum.DISTRIBUTION_ACCORDING_TO_LATEST_EFAS) {
    return {
      datasets: [
        {
          data: [1, 1, 1, 1, 1],
          backgroundColor: ['#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0'],
          dataLabelsClasses: ['green', 'yellow', 'red', 'orange', 'pending'],
          projectQuantity: [0, 0, 0, 0, 0],
          dataLabelsTranslations: [
            'clean',
            'qualified_opinion',
            'adverse_opinion',
            'disclaimer_of_opinion',
            'pending',
          ],
          conceptTranslation: { singular: 'project', plural: 'menu_projects' },
        },
      ],
      labels: ['', '', '', '', ''],
      columns: 1,
    };
  }
  if (indicatorKey === IndicatorKeyEnum.CLASSIFICATION_OF_ACQUISITION_PLANS) {
    return {
      datasets: [
        {
          data: [1, 1, 1],
          backgroundColor: ['#D0D5E0', '#D0D5E0', '#D0D5E0'],
          dataLabelsClasses: ['satisfactory', 'alert', 'problem'],
          projectQuantity: [0, 0, 0],
          dataLabelsTranslations: ['satisfactory', 'alert', 'problem'],
          conceptTranslation: { singular: 'last_plan', plural: 'last_plans' },
        },
      ],
      labels: ['', '', ''],
      columns: 1,
    };
  }
  if (indicatorKey === IndicatorKeyEnum.PMR_STATE_DISTRIBUTION) {
    return {
      datasets: [
        {
          data: [1, 1, 1, 1],
          backgroundColor: ['#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0'],
          dataLabelsClasses: ['satisfactory', 'alert', 'problem', 'not-assigned'],
          projectQuantity: [0, 0, 0, 0],
          dataLabelsTranslations: ['satisfactory', 'alert', 'problem', 'not_assigned_initials'],
          conceptTranslation: { singular: 'project', plural: 'menu_projects' },
        },
      ],
      labels: ['', '', '', ''],
      columns: 1,
    };
  }
  if (indicatorKey === IndicatorKeyEnum.TCP_PERFORMANCE) {
    return {
      datasets: [
        {
          data: [1, 1, 1, 1],
          backgroundColor: ['#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0'],
          dataLabelsClasses: ['satisfactory', 'alert', 'problem', 'not-assigned'],
          projectQuantity: [0, 0, 0, 0],
          dataLabelsTranslations: ['satisfactory', 'alert', 'problem', 'not_applicable'],
          conceptTranslation: { singular: 'project', plural: 'menu_projects' },
        },
      ],
      labels: ['', '', '', ''],
      columns: 1,
    };
  }
  if (indicatorKey === IndicatorKeyEnum.ESG_DISTRIBUTION_ACCORDING_TO_ENVIRONMENTAL_AND_SOCIAL_CATEGORY) {
    return {
      datasets: [
        {
          data: [1, 1, 1, 1, 1, 1],
          backgroundColor: ['#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0', '#D0D5E0'],
          dataLabelsClasses: ['chambray', 'indigo', 'cornflower-blue-01', 'anakiwa-02', 'periwinkle', 'silver'],
          projectQuantity: [0, 0, 0, 0, 0, 0],
          dataLabelsTranslations: ['A', 'B', 'FI', 'B13', 'C', 'Not_Assigned'],
          conceptTranslation: { singular: 'project', plural: 'menu_projects' },
        },
      ],
      labels: ['', '', '', '', '', ''],
      columns: 2,
    };
  }

  return {
    datasets: [
      {
        data: [1],
        backgroundColor: ['#D0D5E0'],
        dataLabelsClasses: [],
        projectQuantity: [],
        dataLabelsTranslations: [],
        conceptTranslation: { plural: '', singular: '' },
      },
    ],
    labels: [],
    columns: 1,
  };
};

const emptyProgressData = () => ({
  progress: 0,
  disbursed: 0,
  pending: 0,
  planned: 0,
  currency: '',
});

const emptyProgressProjectionData = () => ({
  progress: 0,
  disbursed: 0,
  pending: 0,
  currency: '',
  original_project: 0,
  current_project: 0,
  detour: 0,
  percentage: 0,
  currency_project: '',
});

const emptyProgressChartData = (indicatorKey?: string) => {
  const placeholderPercentage = 10;

  switch (indicatorKey) {
    case IndicatorKeyEnum.PMR_VALIDATION_STATE:
      return [
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          supLabelTranslation: '',
          label: 'pmr_validation_state_draft',
          percentage: placeholderPercentage,
          trailColor: '#F1F3F8',
          trailWidth: 10,
          strokeWidth: 10,
          categorization: 'DRAFT',
        },
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          supLabelTranslation: 'sent_to',
          label: 'pmr_validation_state_chief_of_operations',
          percentage: placeholderPercentage,
          trailColor: '#F1F3F8',
          trailWidth: 10,
          strokeWidth: 10,
          categorization: 'CHIEF_OF_OPERATIONS',
        },
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          supLabelTranslation: 'sent_to',
          label: 'pmr_validation_state_chief_of_divisions',
          percentage: placeholderPercentage,
          trailColor: '#F1F3F8',
          trailWidth: 10,
          strokeWidth: 10,
          categorization: 'CHIEF_OF_DIVISIONS',
        },
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          supLabelTranslation: 'sent_to',
          label: 'pmr_validation_state_representative',
          percentage: placeholderPercentage,
          trailColor: '#F1F3F8',
          trailWidth: 10,
          strokeWidth: 10,
          categorization: 'REPRESENTATIVE',
        },
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          supLabelTranslation: 'validation',
          label: 'completed_female',
          percentage: placeholderPercentage,
          trailColor: '#F1F3F8',
          trailWidth: 10,
          strokeWidth: 10,
          categorization: 'VALIDATED',
        },
      ];
    case IndicatorKeyEnum.GCM_ALERTS:
      return [
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          label: 'in_cancellation_en',
          percentage: placeholderPercentage,
          status: 0,
        },
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          label: 'warning_en',
          percentage: placeholderPercentage,
          status: 1,
        },
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          label: 'issue_en',
          percentage: placeholderPercentage,
          status: 2,
        },
      ];
    default:
      return [];
  }
};

const emptyDoubleDataProgressChartData = (indicatorKey?: string) => {
  const placeholderPercentage = 10;

  if (indicatorKey === IndicatorKeyEnum.PCR_NEXT_PRESENTATION) {
    return {
      thirdDataGroup: [
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          supLabelTranslation: '',
          label: 'new_PCR_in_progress',
          percentage: placeholderPercentage,
          trailColor: '#F7F8FA',
          trailWidth: 10,
          strokeWidth: 10,
          categorization: 'PendindPublication',
        },
        {
          color: '#D0D5E0',
          value: 0,
          valueSuffix: 'menu_projects',
          supLabelTranslation: '',
          label: 'new_PRC_very_likely_to_start_in_the_short_term',
          percentage: placeholderPercentage,
          trailColor: '#F7F8FA',
          trailWidth: 10,
          strokeWidth: 10,
          categorization: 'VeryHigh',
        },
      ],
      secondDataGroup: [],
      firstDataGroup: [],
    };
  }

  return {
    thirdDataGroup: [],
    secondDataGroup: [],
    firstDataGroup: [],
  };
};

const emptyMultiBarData = () => {
  const placeholderData = [1, 1, 1, 1, 1];
  const placeholderColor = '#D0D5E0';

  return {
    datasets: [
      { data: placeholderData, backgroundColor: placeholderColor, label: '' },
      { data: placeholderData, backgroundColor: placeholderColor, label: '' },
      { data: placeholderData, backgroundColor: placeholderColor, label: '' },
      { data: placeholderData, backgroundColor: placeholderColor, label: '' },
    ],
    labels: ['0', '0', '0', '0', '0'],
    suggestedMax: 10,
  };
};

const emptyBubbleData = () => ({
  datasets: [
    { data: [], label: '', backgroundColor: 'rgba(0, 0, 0, 0)' },
    { data: [], label: '', backgroundColor: 'rgba(0, 0, 0, 0)' },
    { data: [], label: '', backgroundColor: 'rgba(0, 0, 0, 0)' },
    { data: [], label: '', backgroundColor: 'rgba(0, 0, 0, 0)' },
  ],
});

const emptyMultiLineData = () => ({
  labels: [''],
  datasets: [
    {
      data: [0],
      borderColor: 'rgba(0, 0, 0, 0)',
      backgroundColor: 'transparent',
      borderWidth: 2,
      type: 'line',
      pointRadius: 0,
      lineTension: 0,
      order: 1,
      label: 'line',
    },
  ],
  subLabels: [],
});

const emptyTextDoubleData = (indicatorKey?: string) => {
  switch (indicatorKey) {
    case IndicatorKeyEnum.PROJECTS_LAST_DISBURSEMENT_DATE_CLOSE_12MONTHS_OR_EXPIRED:
      return {
        projectsCloseToExpiration: 0,
        expiredProjects: 0,
        list: [
          { id: 1, value: 0, text: 'projects_close_to_expiration' },
          { id: 2, value: 0, text: 'expired_projects' },
        ],
      };
    case IndicatorKeyEnum.PROJECTS_PENDING_SIGNATURE_OR_LEGISLATIVE_APPROVAL:
      return {
        pendingSignature: 0,
        pendingRatification: 0,
        list: [
          { id: 1, value: 0, text: 'pending_signing_of_contracts' },
          { id: 2, value: 0, text: 'pending_legislative_ratification' },
        ],
      };
    case IndicatorKeyEnum.ACQUISITION_WITH_MILESTONES_PENDING_TO_DATE:
      return {
        pendingSignature: 0,
        pendingRatification: 0,
        list: [
          { id: 1, value: 0, text: 'new_processes_that_should_be_published' },
          { id: 2, value: 0, text: 'new_processes_that_should_be_contracted' },
        ],
      };
    default:
      return { list: [] };
  }
};

const emptyTextDoubleIconData = (indicatorKey?: string) => {
  switch (indicatorKey) {
    case IndicatorKeyEnum.FINANCIAL_PLANS_ABOUT_EXPIRE_EXPIRED:
      return {
        pendingSignature: 0,
        pendingRatification: 0,
        list: [
          { id: 1, iconColor: 'red', value: 0, text: 'new_expired_plans' },
          { id: 2, iconColor: 'yellow', value: 0, text: 'new_due_in_next_30_days_due' },
        ],
      };
    default:
      return { list: [] };
  }
};
